
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Bell, BellOff, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const PushNotifications = () => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const { toast } = useToast();

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
      setIsEnabled(Notification.permission === 'granted');
    }
  }, []);

  const requestPermission = async () => {
    if (!('Notification' in window)) {
      toast({
        title: "Notifications not supported",
        description: "Your browser doesn't support notifications",
        variant: "destructive"
      });
      return;
    }

    const result = await Notification.requestPermission();
    setPermission(result);
    
    if (result === 'granted') {
      setIsEnabled(true);
      toast({
        title: "Notifications enabled!",
        description: "You'll receive alerts about draw results and jackpot updates"
      });
      
      // Send test notification
      new Notification('GoldenTicketLucky Notifications Enabled!', {
        body: 'You will now receive updates about lottery results and special offers.',
        icon: '/favicon.ico'
      });
    }
  };

  const toggleNotifications = () => {
    if (permission === 'granted') {
      setIsEnabled(!isEnabled);
      toast({
        title: isEnabled ? "Notifications disabled" : "Notifications enabled",
        description: isEnabled ? "You won't receive push notifications" : "You'll receive lottery updates"
      });
    } else {
      requestPermission();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-yellow-600" />
          Push Notifications
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <p className="text-gray-600 text-sm">
            Get instant notifications about draw results, jackpot updates, and special offers.
          </p>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isEnabled ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-green-600 font-medium">Enabled</span>
                </>
              ) : (
                <>
                  <BellOff className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-500">Disabled</span>
                </>
              )}
            </div>
            
            <Button
              onClick={toggleNotifications}
              variant={isEnabled ? "outline" : "default"}
              className={isEnabled ? "" : "bg-yellow-600 hover:bg-yellow-700"}
            >
              {isEnabled ? "Disable" : "Enable"} Notifications
            </Button>
          </div>
          
          {permission === 'denied' && (
            <p className="text-red-600 text-sm">
              Notifications are blocked. Please enable them in your browser settings.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PushNotifications;
